import { useCallback, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { useMap } from '@vis.gl/react-google-maps';

import { GET_TASK_BOUNDING_BOX } from '@atom/graph/map';
import { GeoJSONGeometry } from '@atom/types/geojson';
import { BoundingBox, BoundingBoxSubjectType } from '@atom/types/map';

import { getFitBoundsFromShape } from '../mapUtilities';

const SINGLE_LOCATION_ZOOM = 16;

type Frame =
  | { kind: 'box'; box: BoundingBox }
  | { kind: 'location'; geometry: GeoJSONGeometry };

const useTaskBoundingBox = (subjectType: BoundingBoxSubjectType) => {
  const map = useMap();
  const [pending, setPending] = useState<Frame | null>(null);

  const [getBoundingBox] = useLazyQuery(GET_TASK_BOUNDING_BOX, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const applyFrame = (frame: Frame) => {
    if (frame.kind === 'box') {
      const { xMax, yMax, yMin, xMin } = frame.box;
      map.fitBounds({ east: xMax, north: yMax, south: yMin, west: xMin });
      return;
    }

    const isPoint = frame.geometry?.type === 'Point';

    if (isPoint) {
      const [lng, lat] = (frame.geometry as any).coordinates;
      map.panTo({ lat, lng });
      map.setZoom(SINGLE_LOCATION_ZOOM);
      return;
    }

    const bounds = getFitBoundsFromShape(frame.geometry);

    if (bounds) {
      map.fitBounds({
        east: bounds[2],
        north: bounds[3],
        south: bounds[1],
        west: bounds[0],
      });
    }
  };

  useEffect(() => {
    if (map && pending) {
      applyFrame(pending);
      setPending(null);
    }
  }, [map, pending]);

  const frameBoundingBox = useCallback(
    async (input: { taskId: string; workOrderId: string }) => {
      const { data } = await getBoundingBox({
        variables: { input: { ...input, subjectType } },
      });

      if (data?.taskBoundingBox) {
        setPending({ kind: 'box', box: data.taskBoundingBox });
      }
    },
    [getBoundingBox, subjectType],
  );

  const frameLocation = useCallback((geometry: GeoJSONGeometry) => {
    if (geometry) {
      setPending({ kind: 'location', geometry });
    }
  }, []);

  return { frameBoundingBox, frameLocation };
};

export default useTaskBoundingBox;
